import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface NavbarProps {
  onRequestLeads: () => void;
}

export default function Navbar({ onRequestLeads: _onRequestLeads }: NavbarProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goToSignup = () => {
    setMenuOpen(false);
    navigate("/signup");
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="flex items-center gap-2">
          <div className={`w-8 h-8 bg-[#16a34a] rounded-lg flex items-center justify-center flex-shrink-0`}>
            <i className="ri-sun-line text-white text-sm"></i>
          </div>
          <span className={`font-bold text-lg tracking-tight whitespace-nowrap ${scrolled ? "text-gray-900" : "text-white"}`}>
            SolarLead<span className="text-[#16a34a]">Source</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: "How It Works", id: "how-it-works" },
            { label: "What You Get", id: "what-you-get" },
            { label: "Service Area", id: "service-area" },
            { label: "FAQ", id: "faq" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "text-gray-600 hover:text-black"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {item.label}
            </button>
          ))}
          <a
            href="/pricing"
            onClick={(e) => { e.preventDefault(); navigate("/pricing"); }}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              scrolled ? "text-gray-600 hover:text-black" : "text-white/90 hover:text-white"
            }`}
          >
            Pricing
          </a>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              scrolled ? "text-gray-600 hover:text-black" : "text-white/90 hover:text-white"
            }`}
          >
            Blog
          </a>
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              scrolled ? "text-gray-600 hover:text-black" : "text-white/90 hover:text-white"
            }`}
          >
            Contact
          </a>
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="/login"
            onClick={(e) => { e.preventDefault(); navigate("/login"); }}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? "text-gray-700 hover:text-black"
                : "text-white/90 hover:text-white"
            }`}
          >
            Log In
          </a>
          <button
            onClick={goToSignup}
            className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap ${
              scrolled
                ? "bg-black text-white hover:bg-gray-900"
                : "bg-white text-black hover:bg-white/90"
            }`}
          >
            Get Started
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? "text-gray-800 ri-close-line" : scrolled ? "text-gray-800 ri-menu-line" : "text-white ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-4">
          {[
            { label: "How It Works", id: "how-it-works" },
            { label: "What You Get", id: "what-you-get" },
            { label: "Service Area", id: "service-area" },
            { label: "FAQ", id: "faq" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-sm font-medium text-gray-700 text-left cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}
          <a
            href="/pricing"
            onClick={(e) => { e.preventDefault(); navigate("/pricing"); setMenuOpen(false); }}
            className="text-sm font-medium text-gray-700 text-left cursor-pointer whitespace-nowrap"
          >
            Pricing
          </a>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); navigate("/blog"); setMenuOpen(false); }}
            className="text-sm font-medium text-gray-700 text-left cursor-pointer whitespace-nowrap"
          >
            Blog
          </a>
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate("/contact"); setMenuOpen(false); }}
            className="text-sm font-medium text-gray-700 text-left cursor-pointer whitespace-nowrap"
          >
            Contact
          </a>
          <a
            href="/login"
            onClick={(e) => { e.preventDefault(); navigate("/login"); setMenuOpen(false); }}
            className="text-sm font-medium text-gray-700 text-left cursor-pointer whitespace-nowrap"
          >
            Log In
          </a>
          <button
            onClick={goToSignup}
            className="bg-black text-white text-sm font-semibold px-5 py-3 rounded-full cursor-pointer whitespace-nowrap"
          >
            Get Started
          </button>
        </div>
      )}
    </nav>
  );
}
